import { IconName, type PopperHeaderProps, useCallbackRef, useDropdownPopper } from '@talos/kyoko';
import { useEffect, useMemo } from 'react';

import { useMarketSelectorContext } from './MarketSelectorContext';
import type { MarketSelectorPanelProps } from './MarketSelectorPanel';
import { MarketSelectorPanelHeaderSuffix } from './MarketSelectorPanelHeaderSuffix';
import { MarketSelectorPanelTitle } from './MarketSelectorPanelTitle';
import {
  BASE_ADVANCED_MARKET_SELECTOR_WIDTH,
  DEFAULT_MARKET_SELECTOR_HEIGHT,
  DEFAULT_MARKET_SELECTOR_WIDTH,
  MARKET_SELECTOR_AGGREGATIONS_WIDTH,
  NARROW_MARKET_SELECTOR_HEIGHT,
} from './tokens';
import type { UseMarketSelectorPanelParams } from './types';
import { getAdditionalPanelWidthFromContributions } from './utils/enrichments';

/** This hook pulls together the parameters to pass into the `<MarketSelectorPanel />` component */
export const useMarketSelectorPanel = ({
  isOpen,
  close,
  panelPlacement,
  panelAnchorRef,
  panelOffset,
}: UseMarketSelectorPanelParams) => {
  const { advancedMode, enrichments, extensions, blotterSearchInput, isNarrow, disableAggregations } =
    useMarketSelectorContext();

  // we need to do some calculations to figure out a good width for our dropdown in the different modes
  const dropdownWidth = useMemo(() => {
    if (isNarrow) {
      return `calc(100vw - ${panelOffset}px)`;
    }

    let width = advancedMode ? BASE_ADVANCED_MARKET_SELECTOR_WIDTH : DEFAULT_MARKET_SELECTOR_WIDTH;
    if (disableAggregations) {
      width -= MARKET_SELECTOR_AGGREGATIONS_WIDTH;
    }
    width += getAdditionalPanelWidthFromContributions(advancedMode, [...enrichments, ...extensions]);

    return `${width}px`;
  }, [advancedMode, enrichments, extensions, isNarrow, panelOffset, disableAggregations]);

  const closePanel = useCallbackRef(() => {
    close();
  });

  const dropdown = useDropdownPopper({
    isOpen,
    offset: panelOffset,
    // padding just controls the distance between the panel and the viewport boundary
    padding: 8,
    referenceElement: panelAnchorRef.current,
    dropdownPlacement: panelPlacement,
    dropdownWidth: dropdownWidth,
    dropdownHeight: isNarrow ? NARROW_MARKET_SELECTOR_HEIGHT : DEFAULT_MARKET_SELECTOR_HEIGHT,
    onClickOutside: closePanel,
    // The advanced mode toggle feels much better if we just update the popper immediately
    immediateContentSizeUpdate: true,
    // We do not want the panel to move around as you scroll in the order form, for example
    reactToRefElementChanges: isNarrow,
    // We have a big panel, and allow popper.js to render us "disjoint" from the anchor element if there is insufficient room
    tetherToRefElement: false,
  });

  // Whenever the panel is opened, we focus the blotterSearchInput (if any)
  // This lets the user instantly start typing without an additional click after opening the market selector.
  useEffect(() => {
    if (isOpen && blotterSearchInput) {
      blotterSearchInput.focus();
    }
  }, [isOpen, blotterSearchInput, advancedMode]);

  const dropdownHeaderProps: PopperHeaderProps = useMemo(() => {
    return {
      icon: isNarrow ? undefined : IconName.Scale,
      title: <MarketSelectorPanelTitle />,
      suffix: <MarketSelectorPanelHeaderSuffix />,
      suffixFlexProps: {
        gap: '0px',
      },
      onCloseClicked: closePanel,
    };
  }, [closePanel, isNarrow]);

  const dropdownProps: MarketSelectorPanelProps['dropdownProps'] = useMemo(
    () => ({
      ...dropdown,
      headerProps: dropdownHeaderProps,
    }),
    [dropdown, dropdownHeaderProps]
  );

  return dropdownProps;
};
